// Deadlines and owners: the filing deadline, chapter and section due dates
// (a section inherits its chapter's, a chapter the filing deadline), the
// Responsible from the RACI, and what is late or due within a week.
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { invalidate, useMutation, useResource } from "../api/resource";
import type { ScheduleItem } from "../api/types";
import { useCurrentUser } from "../auth";
import { ReviewBadge } from "../components/drafting";
import { ErrorState, Notice, PageHeader, SkeletonBlock, StatusBadge } from "../components/ui";
import { keys, useDossier } from "../dossier";
import { w } from "../i18n";

type Filter = "all" | "late" | "soon" | "open";

function blockPath(dossierId: string, code: string) {
  if (code === "SYN") return `/dossiers/${dossierId}/synthese`;
  return code.includes(".") ? `/dossiers/${dossierId}/sections/${code}` : `/dossiers/${dossierId}/chapters/${code}`;
}

function formatDay(value: string | null) {
  if (!value) return "";
  return new Intl.DateTimeFormat(w("locale.number"), { day: "2-digit", month: "short", year: "numeric" }).format(new Date(`${value}T12:00:00`));
}

function DueCell({ item, canEdit, onSet }: { item: ScheduleItem; canEdit: boolean; onSet: (scope: string, date: string | null) => void }) {
  const label = item.due_date ? formatDay(item.due_date) : w("planning.noDate");
  return <div>
    {canEdit
      ? <input type="date" className="ws-input" aria-label={w("planning.dueFor", { code: item.code })} value={item.own_date || ""}
          onChange={e => onSet(item.code, e.target.value || null)} />
      : <span>{label}</span>}
    <div className="ws-small ws-faint">{item.due_source && item.due_source !== "section"
      ? w(`planning.inherited.${item.due_source}`, { date: formatDay(item.due_date) }) : ""}</div>
  </div>;
}

export function PlanningPage() {
  const { dossier } = useDossier();
  const { can } = useCurrentUser();
  const schedule = useResource(keys.schedule(dossier.id), () => api.schedule(dossier.id));
  const set = useMutation(api.setDeadline);
  const [filter, setFilter] = useState<Filter>("all");
  const canEdit = can("approver");
  const data = schedule.data;
  const onSet = async (scope: string, date: string | null) => {
    if (await set.run(dossier.id, scope, date)) invalidate(keys.schedule(dossier.id), keys.audit(dossier.id));
  };
  const items = (data?.items || []).filter(i => filter === "all" || (filter === "late" && i.late)
    || (filter === "soon" && i.due_soon) || (filter === "open" && i.status !== "approved"));

  return <>
    <PageHeader title={w("planning.title")} lead={w("planning.lead")} />
    {schedule.error && <ErrorState title={w("common.genericError")} error={schedule.error} onRetry={schedule.reload} />}
    {set.error && <ErrorState title={w("team.saveError")} error={set.error} />}
    {schedule.loading && <SkeletonBlock lines={10} />}
    {data && <>
      <section className="ws-card ws-steps ws-steps-5" aria-label={w("planning.summary")}>
        <div className="ws-step"><div className="ws-step-label">{w("planning.filing")}</div>
          {canEdit
            ? <input type="date" className="ws-input" aria-label={w("planning.filing")} value={data.filing_deadline || ""}
                onChange={e => onSet("FILING", e.target.value || null)} />
            : <div className="ws-step-value" style={{ fontSize: 18 }}>{data.filing_deadline ? formatDay(data.filing_deadline) : w("planning.noDate")}</div>}</div>
        {([["approved", data.counts.approved, ""], ["late", data.counts.late, " ws-negative"], ["due_soon", data.counts.due_soon, ""],
          ["without_owner", data.counts.without_owner, ""]] as const).map(([key, value, tone]) => <div key={key} className="ws-step">
          <div className="ws-step-label">{w(`planning.kpi.${key}`)}</div>
          <div className={`ws-step-value${value && tone ? tone : ""}`}>{value}<span className="ws-faint" style={{ fontSize: 14 }}>
            {key === "approved" ? ` / ${data.counts.total}` : ""}</span></div></div>)}
      </section>
      {!data.filing_deadline && <div style={{ marginTop: 12 }}><Notice tone="caution">{w("planning.noFiling")}</Notice></div>}
      <div className="ws-row" style={{ margin: "16px 0 12px" }}>
        <label className="ws-small ws-muted" htmlFor="f-planning">{w("report.filter")}</label>
        <select id="f-planning" className="ws-select" value={filter} onChange={e => setFilter(e.target.value as Filter)}>
          {(["all", "late", "soon", "open"] as Filter[]).map(f => <option key={f} value={f}>{w(`planning.filter.${f}`)}</option>)}</select>
      </div>
      <div className="ws-card ws-table-wrap"><table className="ws-table ws-audit-table">
        <thead><tr><th>{w("plan.col.block")}</th><th>{w("planning.owner")}</th><th>{w("planning.due")}</th><th>{w("planning.delay")}</th>
          <th>{w("planning.status")}</th></tr></thead>
        <tbody>{items.map(item => <tr key={item.code}>
          <td style={{ paddingLeft: item.level === 2 ? 24 : 12 }}><Link className="ws-link" to={blockPath(dossier.id, item.code)}>
            <span className="ws-mono">{item.code === "SYN" ? "" : item.code}</span> {item.level === 1 ? <strong>{item.title}</strong> : item.title}</Link></td>
          <td className="ws-small">{item.owner ? item.owner.name : <span className="ws-faint">{w("planning.noOwner")}</span>}</td>
          <td><DueCell item={item} canEdit={canEdit} onSet={onSet} /></td>
          <td>{item.late ? <StatusBadge status="failed" label={w("planning.late", { days: -(item.days_left || 0) })} />
            : item.due_soon ? <StatusBadge status="needs_review" label={w("planning.soon", { days: item.days_left })} />
            : item.status === "approved" ? <StatusBadge status="approved" label={w("planning.done")} /> : null}</td>
          <td><ReviewBadge status={item.status} /></td>
        </tr>)}</tbody></table></div>
      <div style={{ marginTop: 16 }}><Notice tone="neutral">{w("planning.note")}</Notice></div>
    </>}
  </>;
}
